import React, { useState } from 'react';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import {
  Button,
  TextField,
  Typography,
  Alert,
  useTheme,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import MfaStep from './shared/MfaStep';
import MfaSetup from './shared/MfaSetup';
import ForgotPassword from './shared/ForgotPassword';
import NewPasswordSetup from './shared/NewPasswordSetup';
import OtpLogin from './shared/OtpLogin';
import buildingImage from '../assets/Portal Login Page - Orbit.png';
import datacomLogo from '../assets/Data Centres Primary Horizontal Lock-up.png';
import darkDatacomLogo from '../assets/Data Centres Reversed Horizontal Lock-up.png';

function Login(props) {
  const { darkMode } = props;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [view, setView] = useState('login');
  const [userSession, setUserSession] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [expiredTempPassword, setExpiredTempPassword] = useState(false);
  const [showNoLoginDialog, setShowNoLoginDialog] = useState(false);
  const theme = useTheme();

  const resetToLogin = () => {
    setView('login');
    setUserSession(null);
    setErrorMessage('');
    setPassword('');
    setResetEmail('');
    setResetCodeSent(false);
    setExpiredTempPassword(false);
  };

  const handleUserPoolLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    try {
      const user = await Auth.signIn(email, password);

      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // First-time login with temporary password
        setUserSession(user);
        setView('newPassword');
      } else if (user.challengeName === 'MFA_SETUP') {
        // First-time user needs to set up MFA
        setUserSession(user);
        setView('mfaSetup');
      } else if (user.challengeName === 'SMS_MFA' || user.challengeName === 'SOFTWARE_TOKEN_MFA') {
        // Returning user with MFA already configured
        setUserSession(user);
        setView('mfaVerify');
      } else {
        window.location.href = '/'; // Redirect after successful login
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.code === 'UserNotFoundException') {
        setErrorMessage('User does not exist.');
      } else if (error.code === 'NotAuthorizedException') {
        // Check if this is an expired temporary password
        const msg = (error.message || '').toLowerCase();
        if (msg.includes('temporary password has expired')) {
          // Attempt to resend a new temporary password via backend admin API.
          // This calls adminCreateUser with MessageAction: 'RESEND' for users
          // stuck in FORCE_CHANGE_PASSWORD state.
          try {
            await axios.post(
              `${process.env.REACT_APP_AWS_API_EXTERNAL_URL}/auth/resend_temp_password`,
              { email: email.trim() }
            );
            setErrorMessage(
              'Your temporary password has expired. A new temporary password has been sent to your email. Please check your inbox and try again.'
            );
          } catch (resendErr) {
            console.error('Resend temp password error:', resendErr);
            setErrorMessage(
              'Your temporary password has expired and must be reset by an administrator. Please contact Customer Team to receive a new temporary password.'
            );
          }
        } else {
          setErrorMessage('Incorrect username or password.');
        }
      } else if (error.code === 'UserNotConfirmedException') {
        setErrorMessage(
          'Your account is not confirmed. Please check your email for a confirmation link.'
        );
      } else if (error.code === 'PasswordResetRequiredException') {
        setResetEmail(email);
        setResetCodeSent(true);
        setView('forgotPassword');
      } else {
        setErrorMessage('Login failed. Please try again.');
      }
    }
  };

  const handleSSOLogin = () => {
    Auth.federatedSignIn({ provider: 'AzureAD' });
  };

  // Re-initiates sign-in to trigger a new SMS MFA code for the same user.
  const handleResendSms = async () => {
    const user = await Auth.signIn(email, password);
    if (user.challengeName === 'SMS_MFA') {
      setUserSession(user);
    } else {
      throw new Error('Unexpected challenge after resend');
    }
  };

  // Called after the user sets a new password (from temp password flow).
  // If Cognito returns an MFA challenge, handle it; otherwise redirect to portal.
  const handlePasswordSet = async (updatedUser) => {
    if (updatedUser?.challengeName === 'MFA_SETUP') {
      setUserSession(updatedUser);
      setView('mfaSetup');
    } else if (
      updatedUser?.challengeName === 'SMS_MFA' ||
      updatedUser?.challengeName === 'SOFTWARE_TOKEN_MFA'
    ) {
      setUserSession(updatedUser);
      setView('mfaVerify');
    } else {
      window.location.href = '/';
    }
  };

  const renderLoginForm = () => (
    <>
      {errorMessage && (
        <Alert severity="error" style={{ marginBottom: '15px', width: '280px' }}>
          {errorMessage}
        </Alert>
      )}

      <form
        onSubmit={handleUserPoolLogin}
        style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '280px' }}
      >
        <TextField
          label="Email"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <TextField
          label="Password"
          type="password"
          variant="outlined"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => setView('forgotPassword')}
            style={{
              color: theme.palette.text.primary,
              textTransform: 'none',
              fontSize: '0.75rem',
              padding: 0,
              minWidth: 'auto',
            }}
            size="small"
          >
            Forgot Password?
          </Button>
          <Button
            onClick={() => setShowNoLoginDialog(true)}
            style={{
              color: theme.palette.text.primary,
              textTransform: 'none',
              fontSize: '0.75rem',
              padding: 0,
              minWidth: 'auto',
            }}
            size="small"
          >
            Need help?
          </Button>
        </div>
        <Button type="submit" color="primary" variant="contained" size="large">
          Login
        </Button>
      </form>
      <Button
        onClick={() => setView('otpLogin')}
        variant="outlined"
        style={{ color: theme.palette.text.primary, width: '280px', marginTop: '10px' }}
        size="small"
      >
        One-Time Password
      </Button>
      <Divider style={{ width: '280px', margin: '10px 0' }}>or</Divider>
      <Button
        onClick={handleSSOLogin}
        color="primary"
        variant="contained"
        size="large"
        style={{ width: '280px', marginTop: '10px' }}
      >
        Internal Login
      </Button>
    </>
  );

  const renderCurrentView = () => {
    switch (view) {
      case 'forgotPassword':
        return (
          <ForgotPassword
            onBack={resetToLogin}
            initialEmail={resetEmail}
            codeAlreadySent={resetCodeSent}
            expiredTempPassword={expiredTempPassword}
          />
        );

      case 'newPassword':
        return (
          <NewPasswordSetup
            userSession={userSession}
            onPasswordSet={handlePasswordSet}
            onError={() => {}}
          />
        );

      case 'mfaSetup':
        return (
          <MfaSetup
            userSession={userSession}
            onSetupComplete={() => {
              window.location.href = '/';
            }}
          />
        );

      case 'mfaVerify':
        return (
          <MfaStep
            userSession={userSession}
            setErrorMessage={setErrorMessage}
            onResetSuccess={resetToLogin}
            onBack={resetToLogin}
            onResend={handleResendSms}
          />
        );

      case 'otpLogin':
        return <OtpLogin onBack={resetToLogin} />;

      case 'login':
      default:
        return renderLoginForm();
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
      {/* Left side image (2/3 width) */}
      <div style={{ flex: 2 }}>
        <img
          src={buildingImage}
          alt="Datacom Data Centres Building"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>

      {/* Right side login form (1/3 width) */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <img
          src={darkMode ? darkDatacomLogo : datacomLogo}
          alt="Datacom Logo"
          style={{ width: '280px', marginBottom: '20px' }}
        />
        <Typography
          variant="subtitle1"
          style={{
            width: '280px',
            marginBottom: '20px',
            fontWeight: 'normal',
            textAlign: 'center',
            color: theme.palette.text.primary,
          }}
        >
          Welcome to the Datacom Data Centres Customer Portal
        </Typography>

        {renderCurrentView()}

        <Dialog
          open={showNoLoginDialog}
          onClose={() => setShowNoLoginDialog(false)}
          PaperProps={{ style: { borderRadius: 12, maxWidth: 400, padding: '8px' } }}
        >
          <DialogTitle style={{ textAlign: 'center', fontSize: '1.1rem' }}>
            Need Access?
          </DialogTitle>
          <DialogContent className="col gap">
            <Typography variant="overline" sx={{ mt: 2 }}>
              Logging in for the first time
            </Typography>
            <Typography variant="body2">
              If you're logging in for the first time and can't find your login details, select{' '}
              <b>Forgot Password</b>. This will resend your welcome email and generate a new
              temporary password. Alternatively, you can use the <b>One-Time Password (OTP)</b>{' '}
              option to access your account.
            </Typography>
            <Typography variant="overline" sx={{ mt: 2 }}>
              Need help?
            </Typography>
            <Typography variant="body2">
              If you don't have access or need assistance, please contact the Data Centres Customer
              Team at{' '}
              <a
                href="mailto:DCCustomer@datacom.com"
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                DCCustomer@datacom.com
              </a>
            </Typography>
            <Typography
              variant="body2"
              style={{ textAlign: 'center', marginTop: '12px' }}
            ></Typography>
          </DialogContent>
          <DialogActions style={{ paddingBottom: '16px' }}>
            <Button
              onClick={() => setShowNoLoginDialog(false)}
              variant="contained"
              color="primary"
              size="large"
              disableElevation
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default Login;
